import React from "react";
import { View, Text, StyleSheet, TouchableOpacity } from "react-native";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Ionicons } from "@expo/vector-icons";
import Colors from "../../../Resources/Color";
import GlobalStyles from "../../../Resources/Styles";
import { audioFormatTime } from "../../utils/numberUtils";
import ChatManager from "../../Classes/ChatManager";
import MediaManager from "../../Classes/MediaManager";

const ChatCard = observer(({ client }) => {
  const { t, i18n } = useTranslation();

  // Check if we're viewing the current week
  const today = new Date();
  const summaryStartDate = client.summaryStartDate;

  if (!summaryStartDate) return null;

  const weekEnd = new Date(summaryStartDate);
  weekEnd.setDate(weekEnd.getDate() + 7);

  // Only show chat card if today is within the summary week
  if (today < summaryStartDate || today >= weekEnd) {
    return null;
  }

  // Get unread count
  const unreadCount = client.unreadCount || 0;

  // Get sorted messages
  const sortedMessages = client.messages
    ? [...client.messages].sort((a, b) => a.timestamp - b.timestamp)
    : [];

  // Get unread messages (last N messages based on unreadCount)
  const unreadMessages =
    unreadCount > 0 ? sortedMessages.slice(-unreadCount) : [];

  // Separate text messages and video messages
  const textMessages = unreadMessages.filter(
    (message) => message.type !== "video" && !message.isFromCoach
  );
  const videoMessages = unreadMessages.filter(
    (message) => message.type === "video" && !message.isFromCoach
  );

  // Don't show chat card if there are no messages
  if (textMessages.length === 0 && videoMessages.length === 0) {
    return null;
  }

  // Limit to last 2 messages for display
  const visibleTextMessages = textMessages.slice(-2).reverse();
  const hiddenTextCount = textMessages.length - visibleTextMessages.length;

  const visibleVideoMessages = videoMessages.slice(-2).reverse();
  const hiddenVideoCount = videoMessages.length - visibleVideoMessages.length;

  const chatManager = ChatManager.getInstance();
  const mediaManager = MediaManager.getInstance();
  const coach = client.coach;

  const handleChatPress = () => {
    coach.setProperty("selectedChatId", client.id);
    chatManager.setProperty("selectedReplyToId", null);
    mediaManager.closeMediaPlayer();
    coach.setProperty("showingChatList", true);
  };

  const handleVideoPress = (message) => {
    mediaManager.showMediaPlayer(message, undefined);
    chatManager.setProperty("selectedReplyToId", null);
    coach.setProperty("selectedChatId", client.id);
    coach.setProperty("showingChatList", true);
  };

  return (
    <View style={GlobalStyles.Card}>
      <View style={GlobalStyles.CardHeader}>
        <View style={GlobalStyles.CardHeaderMain}>
          <View style={[GlobalStyles.CardIcon, GlobalStyles.SummaryCardIcon]}>
            <Ionicons
              name="chatbubble-outline"
              size={16}
              color={Colors.grayColor}
            />
          </View>
          <Text style={GlobalStyles.CardTitle}>{t("chatTitle-string")}</Text>
        </View>
      </View>

      <View style={styles.inboxSections}>
        {/* Messages Section */}
        {textMessages.length > 0 && (
          <View>
            <View style={styles.inboxSectionHeader}>
              <Text style={styles.inboxSectionTitle}>
                {t("messagesSection-string")}
              </Text>
              <View style={[styles.inboxBadge, styles.inboxBadgeAlert]}>
                <Text style={styles.inboxBadgeAlertText}>
                  {textMessages.length} {t("new-string")}
                </Text>
              </View>
            </View>
            <View style={styles.inboxList}>
              {visibleTextMessages.map((message, index) => (
                <TouchableOpacity
                  key={message.id || index}
                  style={styles.inboxItem}
                  onPress={handleChatPress}
                >
                  <View style={styles.inboxItemBorder} />
                  <View style={styles.inboxItemMain}>
                    {/* Audio Message */}
                    {message.type === "audio" ? (
                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                        }}
                      >
                        <Ionicons
                          name="mic-outline"
                          size={12}
                          color={Colors.blackColor}
                          style={{ marginRight: 4 }}
                        />
                        <Text style={styles.inboxItemTitle}>
                          {audioFormatTime(message.totalTime)}
                        </Text>
                      </View>
                    ) : message.type === "image" ? (
                      /* Image Message */
                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                        }}
                      >
                        <Ionicons
                          name="camera-outline"
                          size={12}
                          color={Colors.blackColor}
                          style={{ marginRight: 4 }}
                        />
                        <Text style={styles.inboxItemTitle} numberOfLines={1}>
                          {message.text || t("photo-string")}
                        </Text>
                      </View>
                    ) : (
                      /* Text Message */
                      <Text style={styles.inboxItemTitle} numberOfLines={2}>
                        {message.text || "Message"}
                      </Text>
                    )}
                    <Text style={styles.inboxItemMeta}>
                      {message.timeAgo(t, i18n.language)}
                    </Text>
                  </View>
                </TouchableOpacity>
              ))}
              {hiddenTextCount > 0 && (
                <TouchableOpacity
                  style={styles.moreMessagesContainer}
                  onPress={handleChatPress}
                >
                  <Text style={styles.moreMessagesText}>
                    {t("moreMessages-string", { count: hiddenTextCount })}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        )}

        {/* Videos Section */}
        {videoMessages.length > 0 && (
          <View>
            <View style={styles.inboxSectionHeader}>
              <Text style={styles.inboxSectionTitle}>
                {t("videosSection-string")}
              </Text>
              <View style={[styles.inboxBadge, styles.inboxBadgeAlert]}>
                <Text style={styles.inboxBadgeAlertText}>
                  {videoMessages.length} {t("new-string")}
                </Text>
              </View>
            </View>
            <View style={styles.inboxList}>
              {visibleVideoMessages.map((message, index) => {
                const exerciseName = message.getSerie?.exercise?.getName(
                  t,
                  i18n.language
                );
                const parts = message.getSerie?.getFormattedTextParts(
                  message.client?.coach?.rirOrRpe,
                  message.client?.coach?.exerciseUnit
                );

                return (
                  <TouchableOpacity
                    key={message.id || index}
                    style={styles.inboxItem}
                    onPress={() => handleVideoPress(message)}
                  >
                    <View style={styles.inboxItemBorder} />
                    <View style={styles.inboxItemMain}>
                      <View style={styles.videoTitleRow}>
                        <View style={styles.videoMetaRow}>
                          <Text style={styles.inboxItemTitle} numberOfLines={1}>
                            {exerciseName || "Exercise"}
                          </Text>
                          {parts && (
                            <>
                              <Text style={styles.videoMetaSeparator}>·</Text>
                              <Text style={styles.videoMetaText}>
                                {parts.weightText}
                              </Text>
                              <Text style={styles.videoMetaText}>
                                {parts.repsText}
                              </Text>
                              <Text
                                style={[
                                  styles.videoMetaText,
                                  { color: parts.rpeColor },
                                ]}
                              >
                                {parts.rpeLabel}
                                {parts.rpeValue}
                              </Text>
                            </>
                          )}
                        </View>
                      </View>
                      <Text style={styles.inboxItemMeta}>
                        {message.timeAgo(t, i18n.language)}
                      </Text>
                    </View>
                  </TouchableOpacity>
                );
              })}
              {hiddenVideoCount > 0 && (
                <TouchableOpacity
                  style={styles.moreMessagesContainer}
                  onPress={handleChatPress}
                >
                  <Text style={styles.moreMessagesText}>
                    {t("moreVideos-string", { count: hiddenVideoCount })}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        )}
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  inboxSections: {
    gap: 8,
  },
  inboxSectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
  },
  inboxSectionTitle: {
    fontSize: 12,
    fontWeight: "600",
    color: Colors.darkgrayColor,
  },
  inboxBadge: {
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    backgroundColor: Colors.lightGrayColor,
  },
  inboxBadgeAlert: {
    borderColor: Colors.bubbleColor,
    backgroundColor: "rgba(63, 123, 141, 0.1)",
  },
  inboxBadgeAlertText: {
    fontSize: 11,
    fontWeight: "600",
    color: Colors.bubbleColor,
  },
  moreMessagesContainer: {
    paddingVertical: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  moreMessagesText: {
    fontSize: 11,
    fontWeight: "500",
    color: Colors.grayColor,
  },
  inboxList: {
    gap: 2,
  },
  inboxItem: {
    flexDirection: "row",
    alignItems: "stretch",
    paddingVertical: 6,
    gap: 8,
    cursor: "pointer",
  },
  inboxItemBorder: {
    width: 4,
    alignSelf: "stretch",
    backgroundColor: Colors.bubbleColor,
    borderRadius: 4,
  },
  inboxItemMain: {
    gap: 4,
    flex: 1,
  },
  inboxItemTitle: {
    fontSize: 13,
    fontWeight: "500",
    color: Colors.blackColor,
  },
  inboxItemMeta: {
    fontSize: 12,
    color: Colors.grayColor,
  },
  videoTitleRow: {
    gap: 4,
  },
  videoMetaRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
  },
  videoMetaSeparator: {
    fontSize: 13,
    color: Colors.blackColor,
    marginHorizontal: 6,
    fontWeight: "500",
  },
  videoMetaText: {
    fontSize: 13,
    color: Colors.blackColor,
    fontWeight: "500",
  },
});

export default ChatCard;
